import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Popover, Typography, ToggleButton, ToggleButtonGroup, TextField } from '@mui/material';
import { SketchPicker } from 'react-color';
import PaletteIcon from '@mui/icons-material/Palette';
import GradientIcon from '@mui/icons-material/AutoAwesome';
import M from 'messages';
import classes from './styles';

const ColorPicker = ({
  label,
  value = '',
  onChange,
  disabled = false,
  error = false,
  helperText = '',
  allowGradient = true,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [colorType, setColorType] = useState('solid');
  const [solidColor, setSolidColor] = useState('#ffffff');
  const [gradientColors, setGradientColors] = useState({
    color1: '#ffffff',
    color2: '#000000',
    direction: 'to right',
  });

  // Parse existing value on component mount
  React.useEffect(() => {
    if (value) {
      if (value.startsWith('linear-gradient')) {
        setColorType('gradient');
        // Parse gradient value to extract colors and direction
        const gradientMatch = value.match(/linear-gradient\(([^,]+),\s*([^,]+),\s*([^)]+)\)/);
        if (gradientMatch) {
          setGradientColors({
            direction: gradientMatch[1].trim(),
            color1: gradientMatch[2].trim(),
            color2: gradientMatch[3].trim(),
          });
        }
      } else {
        setColorType('solid');
        setSolidColor(value);
      }
    }
  }, [value]);

  const handleClick = useCallback(
    event => {
      if (!disabled) {
        setAnchorEl(event.currentTarget);
      }
    },
    [disabled],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleColorTypeChange = useCallback(
    (event, newType) => {
      if (newType !== null) {
        setColorType(newType);
        // Update the value immediately when switching types
        if (newType === 'solid') {
          onChange(solidColor);
        } else {
          const gradientValue = `linear-gradient(${gradientColors.direction}, ${gradientColors.color1}, ${gradientColors.color2})`;
          onChange(gradientValue);
        }
      }
    },
    [solidColor, gradientColors, onChange],
  );

  const handleSolidColorChange = useCallback(
    color => {
      setSolidColor(color.hex);
      if (colorType === 'solid') {
        onChange(color.hex);
      }
    },
    [colorType, onChange],
  );

  const handleGradientColorChange = useCallback(
    (colorKey, color) => {
      const newGradientColors = {
        ...gradientColors,
        [colorKey]: color.hex,
      };
      setGradientColors(newGradientColors);
      if (colorType === 'gradient') {
        const gradientValue = `linear-gradient(${newGradientColors.direction}, ${newGradientColors.color1}, ${newGradientColors.color2})`;
        onChange(gradientValue);
      }
    },
    [gradientColors, colorType, onChange],
  );

  const handleDirectionChange = useCallback(
    event => {
      const newDirection = event.target.value;
      const newGradientColors = {
        ...gradientColors,
        direction: newDirection,
      };
      setGradientColors(newGradientColors);
      if (colorType === 'gradient') {
        const gradientValue = `linear-gradient(${newDirection}, ${gradientColors.color1}, ${gradientColors.color2})`;
        onChange(gradientValue);
      }
    },
    [gradientColors, colorType, onChange],
  );

  const getPreviewStyle = () => {
    const baseStyle = {
      width: '100%',
      height: '40px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      cursor: disabled ? 'default' : 'pointer',
    };

    if (value) {
      if (value.startsWith('linear-gradient')) {
        return {
          ...baseStyle,
          background: value,
        };
      } else {
        return {
          ...baseStyle,
          backgroundColor: value,
        };
      }
    }

    return {
      ...baseStyle,
      backgroundColor: 'transparent',
      backgroundImage:
        'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
      backgroundSize: '10px 10px',
      backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
    };
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={classes.container}>
      <Typography variant="body2" sx={classes.label}>
        {label}
      </Typography>

      <Box sx={classes.previewContainer}>
        <Button
          variant="outlined"
          onClick={handleClick}
          disabled={disabled}
          sx={{
            ...classes.previewButton,
            ...(error && classes.errorBorder),
          }}
          startIcon={<PaletteIcon />}>
          <Box sx={getPreviewStyle()} />
        </Button>
      </Box>

      {helperText && (
        <Typography
          variant="caption"
          sx={{
            ...classes.helperText,
            ...(error && classes.errorText),
          }}>
          {helperText}
        </Typography>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}>
        <Box sx={classes.popoverContent}>
          {allowGradient && (
            <Box sx={classes.typeSelector}>
              <ToggleButtonGroup value={colorType} exclusive onChange={handleColorTypeChange} size="small" fullWidth>
                <ToggleButton value="solid">
                  <PaletteIcon sx={classes.toggleIcon} />
                  {M.get('colorPicker.solid')}
                </ToggleButton>
                <ToggleButton value="gradient">
                  <GradientIcon sx={classes.toggleIcon} />
                  {M.get('colorPicker.gradient')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {colorType === 'solid' ? (
            <Box sx={classes.colorPickerContainer}>
              <SketchPicker color={solidColor} onChange={handleSolidColorChange} disableAlpha />
            </Box>
          ) : (
            <Box sx={classes.gradientContainer}>
              <Box sx={classes.gradientDirection}>
                <TextField
                  select
                  label={M.get('colorPicker.direction')}
                  value={gradientColors.direction}
                  onChange={handleDirectionChange}
                  SelectProps={{
                    native: true,
                  }}
                  size="small"
                  fullWidth>
                  <option value="to right">{M.get('colorPicker.directions.toRight')}</option>
                  <option value="to left">{M.get('colorPicker.directions.toLeft')}</option>
                  <option value="to bottom">{M.get('colorPicker.directions.toBottom')}</option>
                  <option value="to top">{M.get('colorPicker.directions.toTop')}</option>
                  <option value="to bottom right">{M.get('colorPicker.directions.toBottomRight')}</option>
                  <option value="to bottom left">{M.get('colorPicker.directions.toBottomLeft')}</option>
                  <option value="to top right">{M.get('colorPicker.directions.toTopRight')}</option>
                  <option value="to top left">{M.get('colorPicker.directions.toTopLeft')}</option>
                </TextField>
              </Box>

              <Box sx={classes.gradientColors}>
                {/* make to be next to each other */}
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', width: '100%' }}>
                  <Typography variant="body2" sx={classes.gradientLabel}>
                    {M.get('colorPicker.startColor')}
                  </Typography>
                  <SketchPicker
                    color={gradientColors.color1}
                    onChange={color => handleGradientColorChange('color1', color)}
                    disableAlpha
                  />

                  <Typography variant="body2" sx={classes.gradientLabel}>
                    {M.get('colorPicker.endColor')}
                  </Typography>
                  <SketchPicker
                    color={gradientColors.color2}
                    onChange={color => handleGradientColorChange('color2', color)}
                    disableAlpha
                  />
                </Box>
              </Box>

              <Box sx={classes.gradientPreview}>
                <Typography variant="body2" sx={classes.gradientLabel}>
                  {M.get('colorPicker.preview')}
                </Typography>
                <Box
                  sx={{
                    height: '40px',
                    borderRadius: '4px',
                    background: `linear-gradient(${gradientColors.direction}, ${gradientColors.color1}, ${gradientColors.color2})`,
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

ColorPicker.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  allowGradient: PropTypes.bool,
};

export default ColorPicker;
